import { ApolloLink, Observable } from '@apollo/client';

// serializationKey -> tail promise of the most recently enqueued operation.
const queues = new Map();

/**
 * Queues network requests for operations that pass context.serializationKey so
 * requests with the same key leave one at a time, in call order. Optimistic
 * responses are unaffected (they apply before the link chain runs), so the UI
 * paints immediately while the wire stays ordered — this is what makes rapid
 * full-replace mutations (stop reorders, assigns) safe against HTTP reordering.
 *
 * An errored or unsubscribed operation releases the queue; it never blocks
 * later operations.
 */
export const serializingLink = new ApolloLink((operation, forward) => {
    const { serializationKey } = operation.getContext();
    if (!serializationKey) {
        return forward(operation);
    }

    return new Observable((observer) => {
        let subscription;
        let cancelled = false;
        let release;
        const current = new Promise((resolve) => {
            release = resolve;
        });

        const previous = queues.get(serializationKey) || Promise.resolve();
        const tail = previous.then(() => current);
        queues.set(serializationKey, tail);
        tail.then(() => {
            if (queues.get(serializationKey) === tail) {
                queues.delete(serializationKey);
            }
        });

        previous.then(() => {
            if (cancelled) {
                release();
                return;
            }
            // Downstream links may have async request handlers (App.js's
            // authMiddleware), making forward() return Promise<Observable> —
            // subscribing to that directly would throw and wedge the queue.
            Promise.resolve(forward(operation)).then(
                (observable) => {
                    if (cancelled) {
                        release();
                        return;
                    }
                    subscription = observable.subscribe({
                        next: (value) => observer.next(value),
                        error: (err) => {
                            release();
                            observer.error(err);
                        },
                        complete: () => {
                            release();
                            observer.complete();
                        },
                    });
                },
                (err) => {
                    release();
                    observer.error(err);
                }
            );
        });

        return () => {
            cancelled = true;
            if (subscription) {
                subscription.unsubscribe();
            }
            release();
        };
    });
});

/**
 * Shared key for the DispatchPlan full-replace mutations (assign, stop
 * reorder/remove/edit). One key for the whole board: these mutations rewrite
 * overlapping route state, volumes are human-gesture scale, and a single queue
 * is the simplest ordering guarantee.
 */
export const DISPATCH_PLAN_SERIALIZATION_KEY = 'dispatch-plan-stops';
